import uniq from 'lodash/uniq';
import {ConditionRelationType, ConditionTypeName, RelevantCondition} from '../biotwin/types';
import {isRelated} from '../biotwin/utils';
import {BiobookPage, BiobookPreferences} from './types';
export const defaultBiobookPreferences: BiobookPreferences = {
  favoritePhraseIds: [],
  hiddenPhraseIds: [],
};
export const defaultMechanismLabelColor = window.getComputedStyle(document.body).getPropertyValue('--color-red-100');
const excludedMatchedConditionNames = ['Adult', 'Child', 'Male', 'Female'];
const multiIndexThreshold = 2;
export const getMultiIndexedBiobookPages = (
  biobookPages: BiobookPage[],
  relevantConditions: RelevantCondition[],
  hasRelation: (conditionId1: number, conditionId2: number) => boolean,
) => {
  return biobookPages.filter((biobookPage) => {
    // filters out matched conditions that are excluded
    const matchedConditionNames = biobookPage.matchedConditionNames.filter(
      (matchedConditionName) => !excludedMatchedConditionNames.includes(matchedConditionName),
    );
    const matchedRelevantConditions = getMatchedRelevantConditions(matchedConditionNames, relevantConditions);
    if (matchedRelevantConditions.length < multiIndexThreshold) {
      return false;
    }
    const numberOfNonRelatedConditions = getNumberOfNonRelatedConditions(matchedRelevantConditions, hasRelation);
    return numberOfNonRelatedConditions >= multiIndexThreshold;
  });
};
export const getDrugMultiIndexedBiobookPages = (
  biobookPages: BiobookPage[],
  relevantConditions: RelevantCondition[],
  hasRelation: (conditionId1: number, conditionId2: number) => boolean,
) => {
  return biobookPages.filter((biobookPage) => {
    const matchedRelevantConditions = getMatchedRelevantConditions(
      biobookPage.matchedConditionNames,
      relevantConditions,
    );
    const matchedMedicationRelevantConditions = matchedRelevantConditions.filter(
      (relevantCondition) => relevantCondition.conditionTypeName === ConditionTypeName.Medications,
    );
    if (matchedMedicationRelevantConditions.length < multiIndexThreshold) {
      return false;
    }
    const numberOfNonRelatedConditions = getNumberOfNonRelatedConditions(
      matchedMedicationRelevantConditions,
      hasRelation,
    );
    return numberOfNonRelatedConditions >= multiIndexThreshold;
  });
};
export function getCompareBiobookPages(
  relevantConditions: RelevantCondition[],
  hasRelation: (conditionId1: number, conditionId2: number) => boolean,
) {
  return (a: BiobookPage, b: BiobookPage) => {
    if (a.mechanismLabelShowOrder !== b.mechanismLabelShowOrder) {
      return a.mechanismLabelShowOrder - b.mechanismLabelShowOrder;
    }
    if (a.managementLabelShowOrder !== b.managementLabelShowOrder) {
      return a.managementLabelShowOrder - b.managementLabelShowOrder;
    }
    const aFilteredMatchedConditionNames = a.matchedConditionNames.filter(
      (conditionName) => !excludedMatchedConditionNames.includes(conditionName),
    );
    const bFilteredMatchedConditionNames = b.matchedConditionNames.filter(
      (conditionName) => !excludedMatchedConditionNames.includes(conditionName),
    );
    const aNumberOfNonRelatedConditions = getNumberOfNonRelatedConditions(
      getMatchedRelevantConditions(aFilteredMatchedConditionNames, relevantConditions),
      hasRelation,
    );
    const bNumberOfNonRelatedConditions = getNumberOfNonRelatedConditions(
      getMatchedRelevantConditions(bFilteredMatchedConditionNames, relevantConditions),
      hasRelation,
    );
    if (aNumberOfNonRelatedConditions !== bNumberOfNonRelatedConditions) {
      return bNumberOfNonRelatedConditions - aNumberOfNonRelatedConditions;
    }
    return b.sourcePublishDate.localeCompare(a.sourcePublishDate);
  };
}
function getNumberOfNonRelatedConditions(
  matchedRelevantConditions: RelevantCondition[],
  hasRelation: (conditionId1: number, conditionId2: number) => boolean,
) {
  // groups matched relevant conditions that have some relation with each other
  const groups = matchedRelevantConditions.map((rc, _i, array) => {
    return array.filter(
      (rc2) =>
        rc2 === rc ||
        isRelated(rc2, rc, ConditionRelationType.RequiredRelated) ||
        isRelated(rc, rc2, ConditionRelationType.RequiredRelated) ||
        hasRelation(rc.conditionId, rc2.conditionId) ||
        hasRelation(rc2.conditionId, rc.conditionId),
    );
  });

  // generates a unique id for each group
  const groupIds = groups.map((group) => {
    return group
      .map((rc) => rc.conditionId)
      .sort()
      .join(',');
  });

  // gets number of unique groups
  const numberOfUniqueGroups = uniq(groupIds).length;
  return numberOfUniqueGroups;
}
function getMatchedRelevantConditions(matchedConditionNames: string[], relevantConditions: RelevantCondition[]) {
  return relevantConditions.filter(
    (relevantCondition) =>
      (relevantCondition.conditionRelationType === ConditionRelationType.Selected ||
        relevantCondition.conditionRelationType === ConditionRelationType.RequiredRelated) &&
      matchedConditionNames.includes(relevantCondition.conditionName.trim()),
  );
}
